import { useState, useEffect } from 'react';
import axios from 'axios';
import { HelpCircleIcon } from 'lucide-react';
import { EmptyState, LoadingState, Panel } from '@bcecloud/portal-ui';
import { useEventContext } from './useEventContext';
import { getToken } from '@/lib/auth';

/** One FAQ, as the Salesforce-backed endpoint returns it. */
interface FaqItem {
  Id: string;
  Question__c: string;
  Answer__c: string;
  Sort_Order__c: number;
}

/**
 * Event FAQ tab — the program's frequently asked questions.
 */
export function EventFaqTab(): React.ReactElement {
  const { event } = useEventContext();
  const [faqs, setFaqs] = useState<FaqItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function fetchFaqs(): Promise<void> {
      if (!event?.programSalesforceId) {
        setLoading(false);
        return;
      }
      try {
        const token = getToken();
        const { data } = await axios.get(`/api/v3/programs/${event.programSalesforceId}/faq`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setFaqs(data.data || []);
      } catch {
        if (!cancelled) setFaqs([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void fetchFaqs();
    return () => {
      cancelled = true;
    };
  }, [event?.programSalesforceId]);

  if (loading) return <LoadingState label="Loading FAQs…" />;

  if (faqs.length === 0) {
    return (
      <Panel>
        <EmptyState
          icon={<HelpCircleIcon aria-hidden className="size-6" />}
          title="No FAQ available"
          message="There are no frequently asked questions for this program yet."
        />
      </Panel>
    );
  }

  return (
    <div className="max-w-3xl">
      <Panel className="p-6">
        <h2 className="text-portal-section font-bold tracking-[-0.4px]">
          Frequently asked questions
        </h2>
        <p className="mt-1 text-portal-tiny text-portal-muted">
          Common questions about this program.
        </p>

        <div className="mt-3 divide-y divide-portal-line">
          {faqs.map((faq) => (
            <details key={faq.Id} className="group py-3">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-portal-small font-bold text-portal-ink [&::-webkit-details-marker]:hidden">
                {faq.Question__c}
                <span
                  aria-hidden="true"
                  className="text-portal-muted transition-transform group-open:rotate-90"
                >
                  ›
                </span>
              </summary>
              <p className="mt-2 max-w-[70ch] text-portal-small leading-[1.65] text-portal-muted">
                {faq.Answer__c}
              </p>
            </details>
          ))}
        </div>
      </Panel>
    </div>
  );
}
